import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NavItem } from '@/app/nav';
import { activeNavPath } from './activeNavPath';

export type MenuType = 'main' | 'others';

type MenuGroups = Record<MenuType, NavItem[]>;

export const submenuKey = (menuType: MenuType, name: string): string =>
    `${menuType}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const submenuKeysOf = (groups: MenuGroups): string[] =>
    (Object.keys(groups) as MenuType[]).flatMap((menuType) =>
        groups[menuType].filter((nav) => nav.subItems).map((nav) => submenuKey(menuType, nav.name))
    );

const activeSubmenuKeyOf = (groups: MenuGroups, pathname: string): string | null => {
    const activePath = activeNavPath([...groups.main, ...groups.others], pathname);
    if (!activePath) return null;
    for (const menuType of Object.keys(groups) as MenuType[]) {
        const match = groups[menuType].find((nav) =>
            nav.subItems?.some((subItem) => subItem.path === activePath)
        );
        if (match) return submenuKey(menuType, match.name);
    }
    return null;
};

export function useSidebarSubmenu(mainItems: NavItem[], otherItems: NavItem[], pathname: string) {
    const groups = useMemo(
        () => ({ main: mainItems, others: otherItems }),
        [mainItems, otherItems]
    );
    const activeKey = useMemo(() => activeSubmenuKeyOf(groups, pathname), [groups, pathname]);
    const visibleKeysSignature = useMemo(() => submenuKeysOf(groups).join('|'), [groups]);

    const [openKey, setOpenKey] = useState<string | null>(null);
    const [panelHeights, setPanelHeights] = useState<Record<string, number>>({});
    const panelRefs = useRef<Record<string, HTMLDivElement | null>>({});

    useEffect(() => {
        setOpenKey(activeKey);
    }, [pathname, activeKey]);

    useEffect(() => {
        const visibleKeys = visibleKeysSignature.split('|');
        setOpenKey((current) => (current && visibleKeys.includes(current) ? current : null));
    }, [visibleKeysSignature]);

    const toggle = useCallback((key: string) => {
        setOpenKey((current) => (current === key ? null : key));
    }, []);

    const openKeyRef = useRef(openKey);
    openKeyRef.current = openKey;

    const measure = useCallback((key: string, panel: HTMLDivElement) => {
        const height = panel.scrollHeight || 0;
        setPanelHeights((prev) => (prev[key] === height ? prev : { ...prev, [key]: height }));
    }, []);

    // A panel that mounts already open (say, on expanding the sidebar) is measured as it registers.
    const registerPanel = useCallback(
        (key: string, el: HTMLDivElement | null) => {
            panelRefs.current[key] = el;
            if (el && openKeyRef.current === key) measure(key, el);
        },
        [measure]
    );

    useEffect(() => {
        const panel = openKey ? panelRefs.current[openKey] : null;
        if (openKey && panel) measure(openKey, panel);
    }, [openKey, measure]);

    return { openKey, toggle, registerPanel, panelHeight: (key: string) => panelHeights[key] ?? 0 };
}
